/**
 * Users settings — paginated table + invite / activate / resend welcome.
 *
 * Local auth (`supports_password_login`): invite creates pending user; welcome
 * email includes a set-password link. Entra: invite creates pending users; Microsoft SSO.
 */
import React, { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Post, v1Patch, v1Delete } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDateTimeIST, displayPersonName } from '@/lib/format';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusPill } from '@/components/StatusPill';
import { Avatar } from '@/components/Avatar';
import { Plus, Mail, ShieldCheck, Search, UserCog, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { toastEmailDispatch } from '@/lib/emailDispatchToast';
import {
  DataTablePagination,
  usePagedQueryParams,
} from '@/components/DataTablePagination';

const ROLES = [
  { value: 'admin', desc: 'Full configuration + user management' },
  { value: 'hr_manager', desc: 'Pipeline management + candidates' },
  { value: 'hr_recruiter', desc: 'Candidate operations only' },
  { value: 'interviewer', desc: 'Read-only on candidates assigned to them' },
  { value: 'hiring_manager', desc: 'Scoped to one role + department (read-only)' },
];

const ROLE_LABELS = {
  admin: 'Admin', hr_manager: 'HR Manager', hr_recruiter: 'HR Recruiter',
  interviewer: 'Interviewer', hiring_manager: 'Hiring Manager',
};

const BLANK_INVITE_FORM = {
  full_name: '', email: '', role: 'hr_manager',
  scope_role_id: '', scope_department_id: '',
};

function isInviteFormDirty(form) {
  return form.full_name.trim() !== ''
    || form.email.trim() !== ''
    || form.role !== BLANK_INVITE_FORM.role
    || form.scope_role_id !== ''
    || form.scope_department_id !== '';
}

export default function UsersSettings() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const { data: authCfg } = useQuery({
    queryKey: ['v1-auth-config'],
    queryFn: () => v1Get('/auth/config'),
    staleTime: 60_000,
  });
  const isLocalAuth = Boolean(authCfg?.supports_password_login);
  const authSubtitle = isLocalAuth
    ? 'authentication via email & password'
    : 'authentication via Microsoft 365 SSO';
  const { params, setPage, setPageSize, setFilter, resetFilters } =
    usePagedQueryParams({ initial: { q: '', role: 'all', status: 'all' } });
  const [searchInput, setSearchInput] = useState(() => params.q || '');

  React.useEffect(() => {
    const t = setTimeout(() => {
      if ((params.q || '') !== searchInput) setFilter('q', searchInput);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const qs = new URLSearchParams();
  qs.set('paginated', 'true');
  qs.set('page', params.page);
  qs.set('page_size', params.page_size);
  if (params.q) qs.set('q', params.q);
  if (params.role && params.role !== 'all') qs.set('role', params.role);
  if (params.status && params.status !== 'all') qs.set('status', params.status);

  const { data, isFetching } = useQuery({
    queryKey: ['v1-users', qs.toString()],
    queryFn: () => v1Get(`/users?${qs.toString()}`),
    keepPreviousData: true,
  });
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 0;

  const [open, setOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardConfirmedRef = useRef(false);
  const [form, setForm] = useState({ ...BLANK_INVITE_FORM });
  const [inviteEmailError, setInviteEmailError] = useState('');
  const [confirmUser, setConfirmUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editRoleUser, setEditRoleUser] = useState(null);
  const [roleForm, setRoleForm] = useState({
    role: 'hr_manager',
    scope_role_id: '',
    scope_department_id: '',
  });

  const openRoleDialog = (u) => {
    setEditRoleUser(u);
    setRoleForm({
      role: u.role,
      scope_role_id: u.scope_role_id || '',
      scope_department_id: u.scope_department_id || '',
    });
  };

  const changeRole = async () => {
    if (!editRoleUser) return;
    if (roleForm.role === 'hiring_manager' && (!roleForm.scope_role_id || !roleForm.scope_department_id)) {
      return toast.error('Hiring Manager requires both Role and Department scope.');
    }
    try {
      const payload = { role: roleForm.role };
      if (roleForm.role === 'hiring_manager') {
        payload.scope_role_id = roleForm.scope_role_id;
        payload.scope_department_id = roleForm.scope_department_id;
      }
      await v1Patch(`/users/${editRoleUser.id}`, payload);
      qc.invalidateQueries({ queryKey: ['v1-users'] });
      toast.success(`${editRoleUser.full_name} is now ${ROLE_LABELS[roleForm.role] || roleForm.role}.`);
      setEditRoleUser(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not change role.');
    }
  };

  // Master data for HM scope dropdowns
  const { data: rolesOptions = [] } = useQuery({
    queryKey: ['v1-master-roles'],
    queryFn: () => v1Get('/master/roles'),
    staleTime: 60_000,
  });
  const { data: deptsOptions = [] } = useQuery({
    queryKey: ['v1-master-departments'],
    queryFn: () => v1Get('/master/departments'),
    staleTime: 60_000,
  });

  const resetInviteForm = () => {
    setForm({ ...BLANK_INVITE_FORM });
    setInviteEmailError('');
  };

  const handleInviteDialogOpenChange = (nextOpen) => {
    if (!nextOpen) {
      if (isInviteFormDirty(form)) {
        setOpen(false);
        setDiscardConfirmOpen(true);
        return;
      }
      resetInviteForm();
    }
    setOpen(nextOpen);
  };

  const handleDiscardConfirmOpenChange = (nextOpen) => {
    setDiscardConfirmOpen(nextOpen);
    if (!nextOpen) {
      if (discardConfirmedRef.current) {
        discardConfirmedRef.current = false;
        return;
      }
      if (isInviteFormDirty(form)) {
        setOpen(true);
      }
    }
  };

  const confirmDiscardInviteForm = () => {
    discardConfirmedRef.current = true;
    resetInviteForm();
    setOpen(false);
  };

  const invite = async () => {
    const fullName = form.full_name.trim();
    if (!fullName || !form.email) return toast.error('Name and email are required');
    if (form.role === 'hiring_manager' && (!form.scope_role_id || !form.scope_department_id)) {
      return toast.error('Hiring Manager requires both Role and Department scope.');
    }
    try {
      const payload = {
        full_name: fullName,
        email: form.email,
        role: form.role,
      };
      if (form.role === 'hiring_manager') {
        payload.scope_role_id = form.scope_role_id;
        payload.scope_department_id = form.scope_department_id;
      }
      const created = await v1Post('/users', payload);
      qc.invalidateQueries({ queryKey: ['v1-users'] });
      if (created?.email_dispatch && created.email_dispatch.status !== 'sent') {
        toast.success(
          isLocalAuth
            ? `${fullName} invited (pending until they set a password).`
            : `${fullName} invited.`,
        );
      }
      toastEmailDispatch(created?.email_dispatch, {
        sentMessage: isLocalAuth
          ? `Set-password email sent to ${form.email}`
          : `Welcome email sent to ${form.email}`,
      });
      setOpen(false);
      resetInviteForm();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'string' && /email/i.test(detail) && /already exists/i.test(detail)) {
        setInviteEmailError(detail);
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Could not create user.');
    }
  };

  const resendWelcome = async (u) => {
    try {
      const res = await v1Post(`/users/${u.id}/resend-welcome`, {});
      toastEmailDispatch(res?.email_dispatch, {
        sentMessage: isLocalAuth
          ? `Set-password email re-sent to ${u.email}`
          : `Welcome email re-sent to ${u.email}`,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not resend welcome email.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      await v1Delete(`/users/${deleteUser.id}`);
      qc.invalidateQueries({ queryKey: ['v1-users'] });
      toast.success(`${deleteUser.full_name} has been deleted.`);
      setDeleteUser(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not delete user.');
    } finally {
      setDeleting(false);
    }
  };

  const hasActiveFilters =
    (params.q || '') !== '' || params.role !== 'all' || params.status !== 'all';

  const inviteFlowOpen = open || discardConfirmOpen;

  const openInviteDialog = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setOpen(true);
  };

  const focusInviteNameInput = (event) => {
    event.preventDefault();
    const nameInput = event.currentTarget.querySelector('[data-testid="user-name-input"]');
    nameInput?.focus();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        subtitle={`${total.toLocaleString()} user${total === 1 ? '' : 's'} · ${authSubtitle}`}
        actions={
          <Button className="gap-1.5" onClick={openInviteDialog} data-testid="create-user-button">
            <Plus className="h-4 w-4" /> Add user
          </Button>
        }
      />

      <Card className="card-elevated">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                readOnly={inviteFlowOpen}
                tabIndex={inviteFlowOpen ? -1 : 0}
                aria-hidden={inviteFlowOpen}
                className="pl-9"
                data-testid="users-search-input"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={params.role} onValueChange={(v) => setFilter('role', v)}>
                <SelectTrigger className="w-[160px]" data-testid="users-role-filter">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {Object.entries(ROLE_LABELS).map(([code, label]) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={params.status} onValueChange={(v) => setFilter('status', v)}>
                <SelectTrigger className="w-[140px]" data-testid="users-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost" size="sm"
                onClick={() => { setSearchInput(''); resetFilters(); }}
                disabled={!hasActiveFilters}
                data-testid="users-clear-filters-button"
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isFetching && items.length === 0 && (
              <>
                {Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            )}
            {!isFetching && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  {hasActiveFilters
                    ? 'No users match these filters.'
                    : 'No users yet — click “Add user” to invite the first teammate.'}
                </TableCell>
              </TableRow>
            )}
            {items.map((u) => {
              const userLabel = displayPersonName(u.full_name, u.email);
              return (
              <TableRow key={u.id} data-testid="user-row">
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar name={userLabel} size={32} />
                    <span className="font-medium">{userLabel}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">{u.email}</span>
                </TableCell>
                <TableCell>
                  <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-medium">
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                  {u.role === 'hiring_manager' && (u.scope_role_label || u.scope_department_label) && (
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      Scope: {u.scope_role_label || '—'} · {u.scope_department_label || '—'}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {u.last_login_at ? fmtDateTimeIST(u.last_login_at) : '—'}
                  </span>
                </TableCell>
                <TableCell><StatusPill status={u.status} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => openRoleDialog(u)}
                      data-testid="change-user-role-button"
                      title="Change role"
                    >
                      <UserCog className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => resendWelcome(u)}
                      data-testid="resend-welcome-button"
                      title="Resend welcome email"
                    >
                      Resend
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setConfirmUser(u)}
                      data-testid="toggle-user-status"
                    >
                      {u.status === 'active' ? 'Disable' : 'Activate'}
                    </Button>
                    {currentUser?.id !== u.id && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setDeleteUser(u)}
                        data-testid="delete-user-button"
                        title="Delete user"
                        className="text-rose-600 hover:text-rose-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <DataTablePagination
          page={params.page}
          pageSize={params.page_size}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          isLoading={isFetching}
        />
      </Card>

      <Dialog open={open} onOpenChange={handleInviteDialogOpenChange}>
        <DialogContent
          data-testid="add-user-dialog"
          className="sm:max-w-lg"
          onOpenAutoFocus={focusInviteNameInput}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" /> Invite a teammate
            </DialogTitle>
            <DialogDescription>
              {isLocalAuth
                ? 'They will receive a welcome email with a link to set their password. The account stays pending until they complete that step.'
                : 'They will receive a welcome email and be able to sign in via Office 365 SSO. RBAC governs what they can see and do.'}
            </DialogDescription>
          </DialogHeader>
          <form
            id="invite-user-form"
            autoComplete="off"
            className="grid grid-cols-1 gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              invite();
            }}
          >
            <div>
              <label className="text-xs font-medium text-muted-foreground">Full name</label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="e.g. Priya Sharma"
                name="hireiq-invite-full-name"
                autoComplete="off"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Work email</label>
              <Input
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  if (inviteEmailError) setInviteEmailError('');
                }}
                placeholder="priya@kadellabs.com"
                name="hireiq-invite-work-email"
                type="text"
                inputMode="email"
                autoComplete="one-time-code"
                aria-invalid={!!inviteEmailError}
                data-testid="user-email-input"
              />
              {inviteEmailError ? (
                <p className="mt-1 text-xs text-destructive" data-testid="user-email-error">{inviteEmailError}</p>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Role & permissions
              </label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {ROLE_LABELS[r.value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {ROLES.find((r) => r.value === form.role)?.desc}
              </p>
            </div>
            {form.role === 'hiring_manager' && (
              <div className="grid grid-cols-2 gap-3 rounded-xl border bg-primary/[0.04] p-3">
                <div className="col-span-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                    Hiring Manager scope
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    They will only see candidates matching BOTH the role and the department below.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Role</label>
                  <Select
                    value={form.scope_role_id}
                    onValueChange={(v) => setForm({ ...form, scope_role_id: v })}
                  >
                    <SelectTrigger data-testid="user-hm-role-select">
                      <SelectValue placeholder="Pick a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {rolesOptions
                        .filter((r) => r.status === 'active')
                        .map((r) => (
                          <SelectItem key={r.id} value={r.id}>{formatMasterOptionLabel(r)}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Department</label>
                  <Select
                    value={form.scope_department_id}
                    onValueChange={(v) => setForm({ ...form, scope_department_id: v })}
                  >
                    <SelectTrigger data-testid="user-hm-department-select">
                      <SelectValue placeholder="Pick a department" />
                    </SelectTrigger>
                    <SelectContent>
                      {deptsOptions
                        .filter((d) => d.status === 'active')
                        .map((d) => (
                          <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="rounded-xl border bg-secondary/40 p-3">
              <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="h-3 w-3" /> RBAC preview
              </p>
              <ul className="mt-1.5 ml-1 list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
                <li>{form.role === 'admin' ? 'Manage users, pipelines, AI config, and global settings' : 'Configure pipelines and question banks (Admin only)'}</li>
                <li>Access candidates assigned to them{form.role === 'admin' ? ' or anyone' : ''}</li>
                <li>Generate candidate & client links</li>
              </ul>
            </div>
          </form>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => handleInviteDialogOpenChange(false)}>Cancel</Button>
            <Button type="submit" form="invite-user-form" data-testid="user-invite-confirm">Send invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={handleDiscardConfirmOpenChange}>
        <AlertDialogContent data-testid="invite-user-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes. Are you sure you want to discard them?</AlertDialogTitle>
            <AlertDialogDescription>
              Your entered invite details will be lost if you discard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invite-user-continue-editing">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardInviteForm} data-testid="invite-user-discard-confirm">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editRoleUser} onOpenChange={(v) => !v && setEditRoleUser(null)}>
        <DialogContent data-testid="change-role-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-4 w-4 text-primary" /> Change role
            </DialogTitle>
            <DialogDescription>
              Update access for {editRoleUser?.full_name}. They will receive permissions for
              the new role on their next page load.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Role & permissions
              </label>
              <Select
                value={roleForm.role}
                onValueChange={(v) => setRoleForm({ ...roleForm, role: v })}
              >
                <SelectTrigger data-testid="change-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {ROLE_LABELS[r.value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {ROLES.find((r) => r.value === roleForm.role)?.desc}
              </p>
            </div>
            {roleForm.role === 'hiring_manager' && (
              <div className="grid grid-cols-2 gap-3 rounded-xl border bg-primary/[0.04] p-3">
                <div className="col-span-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                    Hiring Manager scope
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    They will only see candidates matching BOTH the role and the department below.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Role</label>
                  <Select
                    value={roleForm.scope_role_id}
                    onValueChange={(v) => setRoleForm({ ...roleForm, scope_role_id: v })}
                  >
                    <SelectTrigger data-testid="change-role-hm-role-select">
                      <SelectValue placeholder="Pick a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {rolesOptions
                        .filter((r) => r.status === 'active')
                        .map((r) => (
                          <SelectItem key={r.id} value={r.id}>{formatMasterOptionLabel(r)}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Department</label>
                  <Select
                    value={roleForm.scope_department_id}
                    onValueChange={(v) => setRoleForm({ ...roleForm, scope_department_id: v })}
                  >
                    <SelectTrigger data-testid="change-role-hm-department-select">
                      <SelectValue placeholder="Pick a department" />
                    </SelectTrigger>
                    <SelectContent>
                      {deptsOptions
                        .filter((d) => d.status === 'active')
                        .map((d) => (
                          <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRoleUser(null)}>Cancel</Button>
            <Button onClick={changeRole} data-testid="change-role-confirm">Save role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!confirmUser}
        onOpenChange={(v) => {
          if (!v) setConfirmUser(null);
        }}
      >
        <DialogContent data-testid="toggle-user-dialog">
          <DialogHeader>
            <DialogTitle>{confirmUser?.status === 'active' ? 'Disable' : 'Activate'} user</DialogTitle>
            <DialogDescription>
              {confirmUser?.status === 'active'
                ? `${confirmUser?.full_name} will lose access to HireIQ.`
                : isLocalAuth
                  ? `${confirmUser?.full_name} will be marked active. If their password expired, they must use Forgot password before they can sign in.`
                  : `${confirmUser?.full_name} will regain access to HireIQ.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmUser(null)}>Cancel</Button>
            <Button
              onClick={async () => {
                const next = confirmUser.status === 'active' ? 'disabled' : 'active';
                try {
                  await v1Patch(`/users/${confirmUser.id}`, { status: next });
                  qc.invalidateQueries({ queryKey: ['v1-users'] });
                  toast.success(`${confirmUser.full_name} ${next === 'active' ? 'activated' : 'disabled'}.`);
                  setConfirmUser(null);
                } catch (e) {
                  const detail = e?.response?.data?.detail;
                  toast.error(typeof detail === 'string' ? detail : 'Could not update user.');
                }
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteUser} onOpenChange={(v) => { if (!deleting && !v) setDeleteUser(null); }}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteUser?.full_name}? They will be removed from
              the user list and will no longer be able to sign in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="delete-user-confirm"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
